'use client';

import { create } from 'zustand';
import type { LensId } from '@/lib/types';
import type { CalendarLayout } from '@/lib/brand';
import type { AppRole } from '@/lib/auth-context';
import { hasRole } from '@/lib/auth-context';
import { syncUrlParams, readUrlParams } from '@/lib/url-sync';
import { CALENDAR_YEAR } from '@/lib/config';
import { type CalendarSystem, isCalendarSystem } from '@/lib/calendar-systems';

type ViewMode = 'month' | 'year' | 'poster';
export type ThemeStyle = 'sunlit-atlas' | 'signal-ledger' | 'ember-glow' | 'neon-drift';

const THEME_STYLE_KEY = 'vacant-day-theme-style';
const LEGACY_THEME_KEY = 'vacant-day-theme';
const CALENDAR_LAYOUT_KEY = 'vacant-day-calendar-layout';
const CALENDAR_SYSTEM_KEY = 'vacant-day-calendar-system';

function isThemeStyle(value: string | null): value is ThemeStyle {
  return value === 'sunlit-atlas' || value === 'signal-ledger' || value === 'ember-glow' || value === 'neon-drift';
}

function isCalendarLayout(value: string | null): value is CalendarLayout {
  return value === 'hybrid' || value === 'grid' || value === 'timeline' || value === 'poster' || value === 'list';
}

function mapLegacyTheme(value: string | null): ThemeStyle | null {
  if (value === 'light') return 'sunlit-atlas';
  if (value === 'dark') return 'signal-ledger';
  return null;
}

interface CalendarState {
  activeLens: LensId;
  activeEntity: string;
  year: number;
  month: number;
  selectedDay: number | null;
  viewMode: ViewMode;
  themeStyle: ThemeStyle;
  calendarLayout: CalendarLayout;
  calendarSystem: CalendarSystem;
  sidebarOpen: boolean;
  panelOpen: boolean;

  // Page language (set from URL slug resolution)
  lang: string;

  // User role (set by useUserContext or admin layout)
  userRole: AppRole;

  // Premium flag — derived from userRole >= 'ptr', kept for backward compat
  isPremium: boolean;

  // Overlay mode (Pro feature)
  isOverlayMode: boolean;
  overlayLenses: LensId[];
  overlayEntities: Record<string, string>; // lensId -> entityId

  setActiveLens: (lens: LensId, defaultEntity: string) => void;
  setActiveEntity: (entity: string) => void;
  setMonth: (year: number, month: number) => void;
  prevMonth: () => void;
  nextMonth: () => void;
  setSelectedDay: (day: number | null) => void;
  goToToday: () => void;
  setViewMode: (mode: ViewMode) => void;
  setThemeStyle: (style: ThemeStyle) => void;
  setCalendarLayout: (layout: CalendarLayout) => void;
  setCalendarSystem: (system: CalendarSystem) => void;
  setSidebarOpen: (open: boolean) => void;
  setPanelOpen: (open: boolean) => void;
  setLang: (lang: string) => void;
  setUserRole: (role: AppRole) => void;
  setIsPremium: (premium: boolean) => void;
  setOverlayMode: (enabled: boolean) => void;
  toggleOverlayLens: (lens: LensId, defaultEntity: string) => void;
  setOverlayEntity: (lens: LensId, entity: string) => void;
}

const DEFAULT_THEME: ThemeStyle = 'sunlit-atlas';
const DEFAULT_LAYOUT: CalendarLayout = 'hybrid';
const DEFAULT_CALENDAR_SYSTEM: CalendarSystem = 'gregorian';

function getSavedThemeStyle(): ThemeStyle {
  if (typeof window === 'undefined') return DEFAULT_THEME;

  const saved = localStorage.getItem(THEME_STYLE_KEY);
  // Migrate: old layout values stored in theme key → ignore them
  if (saved === 'hybrid' || saved === 'event-list') return DEFAULT_THEME;
  // Migrate: festival-board was replaced by ember-glow; fall back to sunlit-atlas (closest warm light)
  if (saved === 'festival-board') {
    localStorage.setItem(THEME_STYLE_KEY, DEFAULT_THEME);
    return DEFAULT_THEME;
  }
  if (isThemeStyle(saved)) return saved;

  const legacyTheme = mapLegacyTheme(localStorage.getItem(LEGACY_THEME_KEY));
  if (legacyTheme) return legacyTheme;

  // Respect OS dark mode preference for first-time visitors
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'ember-glow';
  }

  return DEFAULT_THEME;
}

function getSavedCalendarLayout(pageDefault: CalendarLayout = DEFAULT_LAYOUT): CalendarLayout {
  if (typeof window === 'undefined') return pageDefault;

  const saved = localStorage.getItem(CALENDAR_LAYOUT_KEY);
  if (isCalendarLayout(saved)) return saved;

  // Migrate: old theme key may hold a layout value (from a period when layout was stored in the theme key)
  const oldTheme = localStorage.getItem(THEME_STYLE_KEY);
  if (oldTheme === 'hybrid') return 'hybrid';
  if (oldTheme === 'event-list') return 'list';

  // No saved preference — use the page-context default (e.g. 'timeline' for academic).
  return pageDefault;
}

function getSavedCalendarSystem(fallback: CalendarSystem = DEFAULT_CALENDAR_SYSTEM): CalendarSystem {
  if (typeof window === 'undefined') return fallback;
  const saved = localStorage.getItem(CALENDAR_SYSTEM_KEY);
  if (saved && isCalendarSystem(saved)) return saved;
  return fallback;
}

const urlState = readUrlParams();

export const useCalendarStore = create<CalendarState>((set, get) => ({
  activeLens: (urlState.activeLens as LensId) || 'country',
  activeEntity: (urlState.activeEntity as string) || 'ID',
  year: (urlState.year as number) || CALENDAR_YEAR,
  month: (urlState.month as number) ?? new Date().getMonth(),
  selectedDay: null,
  viewMode: 'month',
  themeStyle: DEFAULT_THEME,
  calendarLayout: DEFAULT_LAYOUT,
  calendarSystem: DEFAULT_CALENDAR_SYSTEM,
  sidebarOpen: false,
  panelOpen: false,
  // Must be a constant, NOT derived from the DOM/window on the client: a
  // client-only branch here makes the client's initial store lang ('zh')
  // differ from the server's ('en'), so store-driven labels (e.g. the calendar
  // system <option>: "公历" vs "Gregorian") mismatch and React regenerates the
  // whole client tree (React #418). useCalendarViewLogic sets the real page
  // lang in a mount effect right after hydration. (almanak-9ddl)
  lang: 'en',
  userRole: 'guest' as AppRole,
  isPremium: false,
  isOverlayMode: false,
  overlayLenses: [],
  overlayEntities: {},

  setActiveLens: (lens, defaultEntity) =>
    set((state) => {
      syncUrlParams({
        activeLens: lens,
        activeEntity: defaultEntity,
        year: state.year,
        month: state.month,
      });
      return { activeLens: lens, activeEntity: defaultEntity, selectedDay: null };
    }),

  setActiveEntity: (entity) =>
    set((state) => {
      syncUrlParams({
        activeLens: state.activeLens,
        activeEntity: entity,
        year: state.year,
        month: state.month,
      });
      return { activeEntity: entity, selectedDay: null };
    }),

  setMonth: (year, month) =>
    set((state) => {
      syncUrlParams({
        activeLens: state.activeLens,
        activeEntity: state.activeEntity,
        year,
        month,
      });
      return { year, month, selectedDay: null };
    }),

  prevMonth: () =>
    set((state) => {
      let newYear = state.year;
      let newMonth = state.month - 1;
      if (newMonth < 0) { newYear--; newMonth = 11; }
      syncUrlParams({
        activeLens: state.activeLens,
        activeEntity: state.activeEntity,
        year: newYear,
        month: newMonth,
      });
      return { year: newYear, month: newMonth, selectedDay: null };
    }),

  nextMonth: () =>
    set((state) => {
      let newYear = state.year;
      let newMonth = state.month + 1;
      if (newMonth > 11) { newYear++; newMonth = 0; }
      syncUrlParams({
        activeLens: state.activeLens,
        activeEntity: state.activeEntity,
        year: newYear,
        month: newMonth,
      });
      return { year: newYear, month: newMonth, selectedDay: null };
    }),

  setSelectedDay: (day) => set({ selectedDay: day }),

  goToToday: () => {
    const now = new Date();
    const newState = { year: now.getFullYear(), month: now.getMonth(), selectedDay: null as number | null };
    set((state) => {
      syncUrlParams({
        activeLens: state.activeLens,
        activeEntity: state.activeEntity,
        year: newState.year,
        month: newState.month,
      });
      return newState;
    });
  },

  setViewMode: (mode) => set({ viewMode: mode }),

  setThemeStyle: (style) =>
    set(() => {
      if (typeof window !== 'undefined') localStorage.setItem(THEME_STYLE_KEY, style);
      return { themeStyle: style };
    }),

  setCalendarLayout: (layout) =>
    set(() => {
      if (typeof window !== 'undefined') localStorage.setItem(CALENDAR_LAYOUT_KEY, layout);
      return { calendarLayout: layout };
    }),

  setCalendarSystem: (system) =>
    set(() => {
      if (typeof window !== 'undefined') localStorage.setItem(CALENDAR_SYSTEM_KEY, system);
      return { calendarSystem: system };
    }),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setPanelOpen: (open) => set({ panelOpen: open }),

  setLang: (lang) => set({ lang }),
  setUserRole: (role) => {
    if (get().userRole === role) return;
    set({ userRole: role, isPremium: hasRole(role, 'ptr') });
  },
  setIsPremium: (premium) => set({ isPremium: premium }),

  setOverlayMode: (enabled) =>
    set((state) => {
      if (enabled && state.overlayLenses.length === 0) {
        // Initialize overlay with current active lens
        return {
          isOverlayMode: true,
          overlayLenses: [state.activeLens],
          overlayEntities: { [state.activeLens]: state.activeEntity },
        };
      }
      return { isOverlayMode: enabled };
    }),

  toggleOverlayLens: (lens, defaultEntity) =>
    set((state) => {
      const exists = state.overlayLenses.includes(lens);
      if (exists) {
        const next = state.overlayLenses.filter((l) => l !== lens);
        const { [lens]: _, ...rest } = state.overlayEntities;
        return {
          overlayLenses: next,
          overlayEntities: rest,
          // Turn off overlay if less than 2 lenses remain
          isOverlayMode: next.length >= 2 ? state.isOverlayMode : false,
        };
      }
      return {
        overlayLenses: [...state.overlayLenses, lens],
        overlayEntities: { ...state.overlayEntities, [lens]: defaultEntity },
      };
    }),

  setOverlayEntity: (lens, entity) =>
    set((state) => ({
      overlayEntities: { ...state.overlayEntities, [lens]: entity },
    })),
}));

/** Call once on mount (useEffect) to sync themeStyle from localStorage without hydration mismatch */
export function hydrateThemeStyle() {
  const saved = getSavedThemeStyle();
  if (saved !== useCalendarStore.getState().themeStyle) {
    useCalendarStore.setState({ themeStyle: saved });
  }
}

/** Call once on mount (useEffect) to sync calendarLayout from localStorage without hydration mismatch */
export function hydrateCalendarLayout(pageDefault: CalendarLayout = DEFAULT_LAYOUT) {
  const saved = getSavedCalendarLayout(pageDefault);
  if (saved !== useCalendarStore.getState().calendarLayout) {
    useCalendarStore.setState({ calendarLayout: saved });
  }
}

/**
 * Call once on mount (useEffect) to sync calendarSystem from localStorage.
 * `pageDefault` is the page-context default (e.g. 'islamic-umalqura' for a Saudi page),
 * computed via getDefaultCalendarSystem(lang, country). User's localStorage value
 * (if any) wins; otherwise falls back to the page default.
 */
export function hydrateCalendarSystem(pageDefault: CalendarSystem = DEFAULT_CALENDAR_SYSTEM) {
  const saved = getSavedCalendarSystem(pageDefault);
  if (saved !== useCalendarStore.getState().calendarSystem) {
    useCalendarStore.setState({ calendarSystem: saved });
  }
}
